/**
 * What the chat is doing right now, as one label (MYAC-1382).
 *
 * One vocabulary for both sources of that label: a tool call seen live on the
 * stream (name + args), and the `activity` the server writes on the
 * `views/chat_turns` row while a turn runs — the same `chat.activity.*` keys
 * with language-neutral args, mirrored server-side in `session_sharding.py`
 * (`activity_label_spec`). Every tool the chat calls has its own label here, so a
 * long turn never shows a bare "Working on it…".
 */

export interface ActivitySpec {
    /** A `chat.activity.*` key. */
    key: string
    /** Language-neutral interpolation args (a filename, a register kind…). */
    args: Record<string, string>
}

const FILE_MAX = 48

/** The last path segment, cut to a readable length. */
export function shortFileName(path: unknown): string {
    const seg = String(path ?? '').trim().split('/').filter(Boolean).pop() ?? ''
    return seg.length > FILE_MAX ? `${seg.slice(0, FILE_MAX - 1)}…` : seg
}

function shortTitle(value: unknown): string {
    const title = String(value ?? '').split(/\s+/).filter(Boolean).join(' ')
    return title.length > FILE_MAX ? `${title.slice(0, FILE_MAX - 1)}…` : title
}

function standardName(id: unknown): string {
    const s = String(id ?? '').toLowerCase()
    if (s.includes('ivdr')) return 'IVDR'
    if (s.includes('mdr')) return 'MDR'
    if (s.includes('13485')) return 'ISO 13485'
    return ''
}

const PLAIN: Record<string, string> = {
    search_documents: 'searching_documents',
    find_documents: 'searching_documents',
    browse_folders: 'browsing_folders',
    list_knowledge_base: 'knowledge_base',
    search_knowledge_base: 'knowledge_base',
    read_knowledge_article: 'knowledge_base',
    knowledge_base: 'knowledge_base',
    list_qms_assessments: 'reading_assessment',
    get_assessment_overview: 'reading_assessment',
    get_assessment_topic: 'reading_assessment',
    get_referential: 'reading_assessment',
    get_assessment_question: 'reading_assessment_answer',
    get_referential_requirement: 'reading_requirement',
    list_assessment_tasks: 'reading_action_plan',
    get_assessment_task: 'reading_action_plan',
    get_referential_action_plan: 'reading_action_plan',
    get_td_tasks: 'reading_action_plan',
    get_td: 'reading_technical_file',
    list_td_products: 'listing_products',
    list_chat_attachments: 'listing_attachments',
    get_procedure_register: 'reading_procedures',
    link_registers: 'linking_registers',
    read_eudamed_record: 'checking_eudamed',
    lookup_actor: 'checking_eudamed',
    list_actor_devices: 'checking_eudamed',
    get_device: 'checking_eudamed',
    list_actor_certificates: 'checking_eudamed',
    list_notified_bodies: 'checking_eudamed',
    list_kanban_tasks: 'reading_tasks',
    get_kanban_task: 'reading_tasks',
    list_project_members: 'reading_members',
    list_authored_documents: 'listing_written_documents',
    read_authored_document: 'reading_written_document',
    board_list: 'reading_activity_log',
    board_read: 'reading_activity_log',
    board_observe: 'reading_activity_log',
    propose_register_change: 'preparing_proposal',
    propose_requirement_repair: 'preparing_proposal',
    propose_reassessment: 'preparing_proposal',
    propose_standard_disposition: 'preparing_proposal',
    // MYAC-1476 — adopt / unadopt a referential (one declaration, two ops).
    propose_referential_change: 'preparing_proposal',
    propose_task_change: 'preparing_proposal',
    propose_task_lifecycle: 'preparing_proposal',
    trigger_register_refresh: 'preparing_proposal',
    ask_user_questions: 'preparing_question',
    post_human_event: 'recording_request',
    get_current_time: 'checking_time',
    // MYAC-1376 — a guard sending the answer back for a correction.
    answer_review: 'finishing',
}

/** Every `chat.activity.*` key this module can produce — guarded against both locales. */
export function activityKeys(): string[] {
    return [...new Set([
        ...Object.values(PLAIN),
        'reading_register', 'reading_document', 'inspecting_document', 'reading_attachment',
        'looking_up_standard', 'writing_document', 'writing_document_untitled', 'working',
        'register_capa', 'register_nc', 'register_supplier', 'register_change_control', 'standards',
    ])]
}

/** The label spec for a tool call; `working` for a tool this file does not know. */
export function activitySpec(name: string, args: Record<string, unknown>): ActivitySpec {
    switch (name) {
        case 'get_qms_register':
            return { key: 'reading_register', args: { register: String(args.kind ?? '').toLowerCase() } }
        case 'read_document':
            return { key: 'reading_document', args: { file: shortFileName(args.path) } }
        case 'inspect_document':
            return { key: 'inspecting_document', args: { file: shortFileName(args.path) } }
        case 'read_attached_document':
            return { key: 'reading_attachment', args: { file: shortFileName(args.filename) } }
        case 'list_standards':
        case 'list_clauses':
        case 'get_clause_text':
            return { key: 'looking_up_standard', args: { standard: standardName(args.standard_id) } }
        case 'write_authored_document': {
            const title = shortTitle(args.title)
            return title
                ? { key: 'writing_document', args: { title } }
                : { key: 'writing_document_untitled', args: {} }
        }
        default:
            return { key: PLAIN[name] ?? 'working', args: {} }
    }
}

type Translate = (key: string, args?: Record<string, unknown>) => string

/**
 * The label in the viewer's language. Register kinds and standards are translated
 * from their language-neutral value; an unknown key falls back to "Working on it…".
 */
export function translateActivity(t: Translate, spec: ActivitySpec): string {
    const args: Record<string, unknown> = { ...spec.args }
    if (spec.key === 'reading_register') {
        const kindKey = `chat.activity.register_${args.register ?? ''}`
        const kind = t(kindKey)
        // Every register is named explicitly; an unknown kind reads as CAPA rather
        // than as a raw key (the historical fallback of the live indicator).
        args.register = kind !== kindKey ? kind : t('chat.activity.register_capa')
    }
    if (spec.key === 'looking_up_standard' && (!args.standard || args.standard === 'the standards')) {
        args.standard = t('chat.activity.standards')
    }
    const fullKey = `chat.activity.${spec.key}`
    const label = t(fullKey, args)
    return label !== fullKey ? label : t('chat.activity.working')
}
